<template>
  <sc-flex :is-column="true">
    <PreviewDocumentDialog
      :opened.sync="previewDialogOpened"
      :preview-type="'pdf'"
      :preview-rotation="previewRotation"
      :preview-loaded="previewLoaded"
      :document-preview-data="previewData"
      :selected-document-link="selectedDocumentLink"
      :hide-move-button="true"
      :hide-edit-button="true"
      :hide-rename-button="true"
    />

    <!-- Partial: Alerts -->
    <sc-alert v-if="taxReturn.status == 'action_required'" type="info">
      <span>
        <b>Skip or Upload Later:</b>
        If you have the K-1 packages available, you can upload them now or
        choose to upload them later after sending the tax return. Currently,
        {{
          sendK1PackagesAfterSignaturesCompleted
            ? 'K-1 packages can be automatically sent to partners after signatures are collected — if name and email are provided'
            : 'K-1 packages will be sent manually by you or your client'
        }}. This setting can be managed in Firm Settings under Customization.
      </span>

      <sc-button-group slot="button-group">
        <sc-button
          type="ghost"
          @click="next"
          :is-disabled="saving"
          icon-right="arrow-right"
          data-test-id="delivery-next-k1-distribution"
        >
          {{ taxReturn.k1_packages.length > 0 ? 'Next' : 'Skip or Add Later' }}
        </sc-button>
      </sc-button-group>
    </sc-alert>

    <sc-alert v-else type="info">
      <span>
        Upload K-1 packages, add partner or member information to distribute to
        partners or members.
      </span>

      <sc-button-group slot="button-group">
        <sc-button
          type="ghost"
          @click="next"
          :is-disabled="saving"
          icon-right="arrow-right"
          data-test-id="delivery-next-k1-distribution"
        >
          Next
        </sc-button>
      </sc-button-group>
    </sc-alert>

    <v-layout class="outer-container" justify-center>
      <v-layout column class="inner-container">
        <v-layout row class="mt-1 flex-grow-none">
          <v-layout row align-center class="my-4 mx-3">
            <h2>K-1 Package Distribution</h2>
            <v-spacer />
            <v-tooltip bottom class="mr-2">
              <v-icon
                slot="activator"
                class="material-icons-outlined primary--text clickable"
                @click="refreshPackages"
              >
                refresh
              </v-icon>
              <span>Refresh</span>
            </v-tooltip>

            <v-tooltip bottom class="mr-2">
              <v-icon
                slot="activator"
                class="material-icons-outlined primary--text clickable"
                @click="exportToExcel"
              >
                file_download
              </v-icon>
              <span>Export to Excel</span>
            </v-tooltip>

            <v-btn
              v-if="taxReturnHasBeenSent"
              color="primary"
              class="text-none"
              @click="bulkSendPackages"
              :disabled="validSelectedPackages.length === 0 || saving"
              :loading="saving"
            >
              <v-icon class="material-icons-outlined" left>
                send
              </v-icon>
              Send Selected{{
                validSelectedPackages.length > 0
                  ? ` (${validSelectedPackages.length})`
                  : ''
              }}
            </v-btn>
          </v-layout>
        </v-layout>

        <!-- Drag and Drop Area -->
        <v-layout
          class="drag-and-drop-area flex-grow-none mx-3"
          @drop.prevent="handleDrop"
          @dragover.prevent
          @dragenter.prevent
          @dragleave.prevent
          @click="pickK1Documents"
        >
          <v-layout
            column
            align-center
            justify-center
            v-if="!uploadingPackages"
          >
            <div>
              <span class="drag-and-drop-text grey--text text--darken-2">
                Drag & drop the PDF files here or
                <span class="clickable underline primary--text">browse</span>
              </span>
            </div>
            <span class="text-caption grey--text mt-1">(Max Size 150MB)</span>
          </v-layout>

          <v-layout column align-center justify-center v-else>
            <v-progress-circular
              indeterminate
              size="32"
              class="ma-2"
              color="primary"
            ></v-progress-circular>
            <div class="mt-2">
              <span>Uploading...</span>
            </div>
          </v-layout>
        </v-layout>

        <v-layout
          column
          class="mx-3 mt-3 import-from-excel-box flex-grow-none"
          v-if="
            taxReturnExperimentalK1EmailImport &&
              taxReturn.k1_packages.length > 0
          "
        >
          <v-layout align-center>
            <v-layout row align-center justify-between>
              <v-icon
                class="material-icons-outlined primary--text text--darken-1"
                >article</v-icon
              >

              <v-layout column class="ml-3">
                <div
                  class="body-1 font-weight-bold primary--text text--darken-2"
                >
                  Have a partner list in Excel?
                </div>
                <div class="body-2 grey--text text--darken-2">
                  Import email addresses in bulk to save time
                </div>
              </v-layout>
            </v-layout>

            <v-btn
              class="text-none ma-0 ml-2"
              @click="pickSpreadsheet"
              color="primary"
              :disabled="uploadingSpreadsheet || uploadingPackages"
            >
              Import from Excel
            </v-btn>
          </v-layout>

          <v-progress-linear
            v-if="uploadingSpreadsheet"
            indeterminate
            color="primary"
            class="mt-3 mb-0"
          />
        </v-layout>

        <!-- Hidden file input -->
        <input
          type="file"
          accept=".pdf"
          style="display: none"
          multiple
          ref="selectedK1Documents"
          @change="onK1DocumentsPicked"
        />

        <!-- Hidden file input for spreadsheet -->
        <input
          type="file"
          accept=".xlsx,.xls"
          style="display: none"
          ref="selectedSpreadsheet"
          @change="onSpreadsheetPicked"
        />

        <v-form ref="form" lazy-validation>
          <v-data-table
            :headers="headers"
            :items="taxReturn.k1_packages"
            :disable-initial-sort="true"
            class="partners-table auto-shrink"
            :pagination.sync="pagination"
            v-model="selectedPackages"
            item-key="document_path"
            :select-all="taxReturnHasBeenSent"
            :loading="saving"
          >
            <template v-slot:items="props">
              <td v-if="taxReturnHasBeenSent">
                <v-checkbox
                  v-model="props.selected"
                  :disabled="saving"
                  hide-details
                />
              </td>
              <td>
                <v-text-field
                  class="default-input"
                  solo
                  v-model="props.item.partner_name"
                  placeholder="Enter partner name"
                  @change="save"
                  @blur="save"
                  hide-details
                  :disabled="packageViewed(props.item)"
                />
              </td>
              <td class="text-xs-left">
                <v-text-field
                  class="default-input"
                  solo
                  v-model="props.item.partner_email"
                  placeholder="Enter partner email"
                  :rules="[rules.validEmail]"
                  @change="save"
                  @blur="save"
                  hide-details
                  :disabled="packageViewed(props.item)"
                />
              </td>
              <td class="text-xs-left">
                <v-btn
                  outline
                  small
                  color="primary"
                  class="text-none mx-0"
                  @click="previewDocument(props.item.document_path)"
                >
                  <v-icon
                    small
                    class="material-icons-outlined mr-1 primary--text"
                    left
                  >
                    file_open
                  </v-icon>
                  <span class="truncated-filename">{{
                    truncateString(props.item.custom_package_name, 20)
                  }}</span>
                </v-btn>
              </td>
              <td class="text-xs-center">
                <v-chip
                  :color="getStatusColor(props.item)"
                  text-color="white"
                  small
                >
                  {{ getStatusLabel(props.item) }}
                </v-chip>
              </td>
              <td class="text-xs-center">
                <v-tooltip bottom>
                  <v-btn
                    slot="activator"
                    v-if="props.item.locked_at"
                    small
                    icon
                    color="warning"
                    class="ma-1"
                    @click="unlockPackage(props.item)"
                    :disabled="saving"
                    :loading="saving"
                  >
                    <v-icon small class="material-icons-outlined">
                      lock_open
                    </v-icon>
                  </v-btn>
                  <span>Unlock</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <v-btn
                    slot="activator"
                    v-if="taxReturnHasBeenSent"
                    small
                    icon
                    color="primary"
                    class="ma-1"
                    @click="sendPackage(props.item)"
                    :disabled="!canSendPackage(props.item) || saving"
                    :loading="saving"
                  >
                    <v-icon small>
                      send
                    </v-icon>
                  </v-btn>
                  <span>{{ getSendButtonTooltip(props.item) }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <v-btn
                    slot="activator"
                    small
                    icon
                    color="error"
                    class="ma-1"
                    @click="remove(props.item)"
                    :disabled="saving"
                  >
                    <v-icon small class="material-icons-outlined">
                      delete
                    </v-icon>
                  </v-btn>
                  <span>Delete</span>
                </v-tooltip>
              </td>
            </template>
          </v-data-table>
        </v-form>
      </v-layout>
    </v-layout>
  </sc-flex>
</template>

<script>
import API from '@/api';
import { mapGetters } from 'vuex';
import axios from 'axios';
import ReadonlyAlert from '@/components/returns/ReadonlyAlert';
import PreviewDocumentDialog from '@/components/dialogs/PreviewDocumentDialog';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import UtilsMixins from '@/mixins/UtilsMixins';
import isEmpty from 'lodash/isEmpty';
import * as XLSX from 'xlsx';

export default {
  components: {
    ReadonlyAlert,
    PreviewDocumentDialog,
  },

  mixins: [FirmPreferenceMixins, UtilsMixins],

  props: {
    currentCompany: {
      type: Object,
      default: () => {},
    },

    taxReturn: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      headers: [
        {
          text: 'Partner name',
          align: 'left',
          value: 'partner_name',
          sortable: true,
        },
        {
          text: 'Partner email',
          value: 'partner_email',
          align: 'left',
          sortable: true,
        },
        {
          text: 'K-1 package',
          value: 'custom_package_name',
          align: 'left',
          sortable: false,
        },
        {
          text: 'Status',
          value: 'status',
          align: 'center',
          sortable: false,
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
        },
      ],

      pagination: {
        rowsPerPage: -1,
      },

      uploadingPackages: false,
      uploadingSpreadsheet: false,
      saving: false,
      refreshing: false,

      rules: {
        required: value => !!value || 'Required.',
        validEmail: value =>
          !value || /.+@.+\..+/.test(value) || 'Email must be valid.',
      },

      // preview package
      previewData: null,
      previewRotation: 0,
      previewLoaded: false,
      previewDialogOpened: false,
      selectedDocumentLink: null,

      selectedPackages: [],
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentUserFirm: 'currentFirm',
    }),

    editable() {
      return (
        this.taxReturn.status === 'action_required' ||
        this.taxReturn.status === 'processing'
      );
    },

    validSelectedPackages() {
      return this.selectedPackages.filter(
        item => item.partner_email && item.partner_email.trim() !== '',
      );
    },

    taxReturnHasBeenSent() {
      return (
        this.taxReturn.status !== 'processing' &&
        this.taxReturn.status !== 'action_required'
      );
    },
  },

  methods: {
    packageViewed(item) {
      return (
        !!item.viewed_at_by_partner ||
        item.consented_to_electronic_delivery === true ||
        item.consented_to_electronic_delivery === false
      );
    },

    next() {
      if (this.validateInputs()) {
        this.$emit('next');
      } else {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Please make sure the email are valid.',
        });
      }
    },

    async handleDrop(event) {
      event.preventDefault();
      const files = event.dataTransfer.files;
      await this.uploadK1Documents(files);
    },

    async uploadK1Documents(files) {
      try {
        this.uploadingPackages = true;

        for (const file of files) {
          const {
            data: { id, upload_link },
          } = await API.taxReturn.getUploadLinkForK1Package({
            companyId: this.currentCompany.id,
          });

          await API.integration.uploadToDropbox(upload_link, file);

          const response = await API.taxReturn.processK1Package({
            fileId: id,
            taxReturnId: this.taxReturn.id,
            documentName: file.name,
            companyId: this.currentCompany.id,
          });

          await this.addPackage(
            response.data.document_name,
            response.data.document_location,
            response.data.partner_name,
            response.data.partner_email,
          );
          console.log(this.taxReturn.k1_packages);
        }
        await this.save();

        this.uploadingPackages = false;
        // Clear the file input value to allow subsequent uploads
        if (this.$refs.selectedK1Documents) {
          this.$refs.selectedK1Documents.value = '';
        }
      } catch (error) {
        console.log(error);
        this.uploadingPackages = false;
        // Clear the file input value even on error
        if (this.$refs.selectedK1Documents) {
          this.$refs.selectedK1Documents.value = '';
        }

        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem uploading the documents.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      }
    },

    async addPackage(packageName, documentPath, partnerName, partnerEmail) {
      const newPackage = {
        custom_package_name: packageName,
        document_path: documentPath,
        partner_name: partnerName,
        partner_email: partnerEmail,
      };

      if (!Array.isArray(this.taxReturn.k1_packages)) {
        this.taxReturn.k1_packages = [];
      }

      this.taxReturn.k1_packages.push(newPackage);
    },

    async remove(k1Package) {
      this.taxReturn.k1_packages = this.taxReturn.k1_packages.filter(
        p => p !== k1Package,
      );

      await this.save();
    },

    validateInputs() {
      return this.$refs.form.validate();
    },

    async save() {
      const response = await axios.patch(`/tax_returns/${this.taxReturn.id}`, {
        ...this.taxReturn,
        k1_packages: this.taxReturn.k1_packages,
      });

      return response.data;
    },

    closePreviewDialog() {
      this.previewData = '';
      this.previewRotation = 0;
      this.previewDialogOpened = false;
    },

    async previewDocument(documentPath) {
      this.previewLoaded = false;
      this.previewType = null;
      this.previewData = null;
      this.previewRotation = 0;

      this.previewDialogOpened = true;

      try {
        const response = await API.integration.downloadDocument({
          companyId: this.currentCompany.id,
          link: documentPath,
        });

        this.previewData = response.data.document.data.link;
        this.selectedDocumentLink = documentPath;
      } catch (e) {
        console.log(e);

        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
          errorToSend: e,
        });
      } finally {
        this.previewLoaded = true;
      }
    },

    getStatusColor(item) {
      if (item.locked_at) {
        return 'error';
      } else if (item.consented_to_electronic_delivery === false) {
        return 'error';
      } else if (item.viewed_at_by_partner || item.marked_delivered_at) {
        return 'success';
      } else if (item.sent_at_to_partner) {
        return 'primary';
      } else {
        return 'warning';
      }
    },

    getStatusLabel(item) {
      if (item.locked_at) {
        return 'Locked';
      } else if (item.consented_to_electronic_delivery === false) {
        return 'Declined electronic delivery';
      } else if (item.marked_delivered_at) {
        const deliveredDate = new Date(item.marked_delivered_at * 1000);
        return `Marked as delivered on ${deliveredDate.toLocaleDateString()}`;
      } else if (item.viewed_at_by_partner) {
        const viewedDate = new Date(item.viewed_at_by_partner * 1000);
        return `Viewed on ${viewedDate.toLocaleDateString()}`;
      } else if (item.sent_at_to_partner) {
        const sentDate = new Date(item.sent_at_to_partner * 1000);
        return `Sent on ${sentDate.toLocaleDateString()}`;
      } else {
        return 'Not Sent';
      }
    },

    canSendPackage(item) {
      return (
        item.consented_to_electronic_delivery !== false &&
        this.hasEmail(item) &&
        this.hasName(item)
      );
    },

    hasEmail(item) {
      return item.partner_email && item.partner_email.trim() !== '';
    },

    hasName(item) {
      return item.partner_name && item.partner_name.trim() !== '';
    },

    async sendPackage(item) {
      if (item.viewed_at_by_partner) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Partner has already viewed the package.',
        });
        return;
      } else if (item.consented_to_electronic_delivery === false) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Partner has declined electronic delivery.',
        });
        return;
      } else if (!this.canSendPackage(item)) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Partner email and name are required to send the package.',
        });
        return;
      }

      try {
        this.saving = true;

        await API.taxReturn.sendK1PackageToPartner({
          company_id: this.currentCompany.id,
          tax_return_id: this.taxReturn.id,
          document_path: item.document_path,
          partner_email: item.partner_email,
          partner_name: item.partner_name,
        });

        // Update the item with sent timestamp
        item.sent_at_to_partner = Math.floor(Date.now() / 1000);

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'K-1 package sent successfully',
        });
      } catch (error) {
        console.error('Error sending K-1 package:', error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Failed to send K-1 package',
          errorToSend: error,
        });
      } finally {
        this.saving = false;
      }
    },

    async unlockPackage(item) {
      if (!item.partner_email) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Partner email is required to unlock the package.',
        });
        return;
      }

      try {
        this.saving = true;

        await API.taxReturn.unlockK1Verification({
          companyId: this.currentCompany.id,
          taxReturnId: this.taxReturn.id,
          documentPath: item.document_path,
          partnerEmail: item.partner_email,
        });

        // Refresh the tax return data to get updated status
        const response = await API.taxReturn.getTaxReturn({
          id: this.taxReturn.id,
          companyId: this.currentCompany.id,
        });
        Object.assign(this.taxReturn, response.data);

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'K-1 package unlocked successfully',
        });
      } catch (error) {
        console.error('Error unlocking K-1 package:', error);

        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Failed to unlock K-1 package';

        this.$store.dispatch('application/setErrorNotification', {
          message: errorMessage,
          errorToSend: error,
        });
      } finally {
        this.saving = false;
      }
    },

    async bulkSendPackages() {
      const validPackages = this.selectedPackages.filter(item =>
        this.canSendPackage(item),
      );

      if (validPackages.length === 0) {
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'No valid packages to send (missing email addresses or names)',
        });
        return;
      }

      try {
        this.saving = true;

        // Send each valid package
        for (const item of validPackages) {
          await API.taxReturn.sendK1PackageToPartner({
            company_id: this.currentCompany.id,
            tax_return_id: this.taxReturn.id,
            document_path: item.document_path,
            partner_email: item.partner_email,
            partner_name: item.partner_name,
          });

          // Update the item with sent timestamp
          item.sent_at_to_partner = Math.floor(Date.now() / 1000);
        }

        // Clear selection
        this.selectedPackages = [];

        this.$store.dispatch('application/setSuccessNotification', {
          message: `Successfully sent ${validPackages.length} K-1 package(s)`,
        });
      } catch (error) {
        console.error('Error sending K-1 packages:', error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Failed to send K-1 packages',
          errorToSend: error,
        });
      } finally {
        this.saving = false;
      }
    },

    async refreshPackages() {
      try {
        this.refreshing = true;

        // Clear any selections when refreshing
        this.selectedPackages = [];

        // Reload the tax return data to get updated K1 packages
        const response = await API.taxReturn.getTaxReturn({
          id: this.taxReturn.id,
          companyId: this.currentCompany.id,
        });

        // Update the tax return with fresh data
        Object.assign(this.taxReturn, response.data);

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'K-1 packages refreshed successfully',
        });
      } catch (error) {
        console.error('Error refreshing K-1 packages:', error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Failed to refresh K-1 packages',
          errorToSend: error,
        });
      } finally {
        this.refreshing = false;
      }
    },

    async pickK1Documents() {
      const input = this.$refs.selectedK1Documents;
      if (input) {
        input.click();
      }
    },

    async onK1DocumentsPicked(event) {
      const files = Array.from(event.target.files);
      if (files.length > 0) {
        await this.uploadK1Documents(files);
      }
      // Clear the input value to allow the same file to be selected again
      event.target.value = '';
    },

    getSendButtonTooltip(item) {
      if (item.consented_to_electronic_delivery === false) {
        return 'Partner has declined electronic delivery.';
      } else if (item.viewed_at_by_partner) {
        return 'Partner has already viewed the package.';
      } else if (!this.canSendPackage(item)) {
        return 'Partner email and name are required to send the package.';
      }
      return 'Send K-1 package';
    },

    async pickSpreadsheet() {
      const input = this.$refs.selectedSpreadsheet;
      if (input) {
        input.click();
      }
    },

    async onSpreadsheetPicked(event) {
      const files = Array.from(event.target.files);
      if (files.length > 0) {
        await this.uploadSpreadsheet(files[0]);
      }
      // Clear the input value to allow the same file to be selected again
      event.target.value = '';
    },

    async uploadSpreadsheet(file) {
      try {
        // check if file ends with .xlsx or .xls
        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'The selected file must have a .xlsx or .xls extension.',
          });
          return;
        }

        this.uploadingSpreadsheet = true;

        const {
          data: { id, upload_link },
        } = await API.taxReturn.getUploadLinkForK1Spreadsheet({
          companyId: this.currentCompany.id,
        });

        await API.integration.uploadToDropbox(upload_link, file);

        const partnerNames = this.taxReturn.k1_packages
          .filter(pkg => pkg.partner_name && pkg.partner_name.trim() !== '')
          .map(pkg => pkg.partner_name);

        const response = await API.taxReturn.processK1Spreadsheet({
          fileId: id,
          taxReturnId: this.taxReturn.id,
          documentName: file.name,
          companyId: this.currentCompany.id,
          partnerNames: partnerNames,
        });

        this.uploadingSpreadsheet = false;

        // Clear the file input value to allow subsequent uploads
        if (this.$refs.selectedSpreadsheet) {
          this.$refs.selectedSpreadsheet.value = '';
        }

        let numberOfEmailImported = 0;

        // Update email address
        response.data.email_address_matches.forEach(match => {
          const pkg = this.taxReturn.k1_packages.find(
            p => p.partner_name === match.partner_name,
          );

          // Only replace email if there is one match
          if (pkg && match.matched_emails.length === 1) {
            pkg.partner_email = match.matched_emails[0];
            numberOfEmailImported += 1;
          }
        });

        this.$forceUpdate();

        if (numberOfEmailImported > 0) {
          this.$store.dispatch('application/setSuccessNotification', {
            message: `Successfully imported ${numberOfEmailImported} email address(es).`,
          });
        } else {
          this.$store.dispatch('application/setWarningNotification', {
            message:
              'Spreadsheet uploaded successfully, but no email addresses were imported.',
          });
        }
      } catch (error) {
        console.log(error);
        this.uploadingSpreadsheet = false;
        // Clear the file input value even on error
        if (this.$refs.selectedSpreadsheet) {
          this.$refs.selectedSpreadsheet.value = '';
        }

        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem uploading the spreadsheet.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      }
    },

    exportToExcel() {
      try {
        // Check if there are any K-1 packages to export
        if (
          !this.taxReturn.k1_packages ||
          this.taxReturn.k1_packages.length === 0
        ) {
          this.$store.dispatch('application/setWarningNotification', {
            message: 'No K-1 packages to export.',
          });
          return;
        }

        // Prepare data for export - only partner_name and email columns
        const exportData = this.taxReturn.k1_packages.map(pkg => ({
          'Partner Name': pkg.partner_name || '',
          Email: pkg.partner_email || '',
          'Package Name': pkg.custom_package_name || '',
        }));

        // Create a new workbook and worksheet
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'K-1 Partners');

        // Set column widths for better readability
        worksheet['!cols'] = [
          { wch: 30 }, // Partner Name column width
          { wch: 35 }, // Email column width
        ];

        // Generate filename with timestamp
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `K1_Partners_${timestamp}.xlsx`;

        // Write the file
        XLSX.writeFile(workbook, filename);

        this.$store.dispatch('application/setSuccessNotification', {
          message: `Successfully exported ${this.taxReturn.k1_packages.length} partner(s) to ${filename}`,
        });
      } catch (error) {
        console.error('Error exporting to Excel:', error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Failed to export to Excel',
          errorToSend: error,
        });
      }
    },
  },

  watch: {},

  async beforeDestroy() {
    await this.save();
  },
};
</script>

<style lang="postcss" scoped>
.outer-container {
  min-height: calc(100dvh - 120px);
  max-height: calc(100dvh - 120px);
  height: 100%;
  overflow: auto;
}

.inner-container {
  flex: auto;
  max-width: 1200px;
  width: 100%;
}

.partners-table {
  margin: 0px 16px 16px;
}

.drag-and-drop-area {
  border: 1.5px dashed #ccc;
  background-color: #f7f7f7;
  border-radius: 8px;
  height: 112px;
  flex-shrink: 0;
  cursor: pointer;
}

.drag-and-drop-text {
  font-size: 16px;
}

.clickable {
  cursor: pointer;
}

.underline {
  text-decoration: underline;
}

.truncated-filename {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}

.import-from-excel-box {
  border: 1px solid #aac7f3;
  border-radius: 8px;
  padding: 16px;
  margin: 0px 16px 16px;
  background: #eaf1fc;
}

.default-input {
  & >>> .v-input__control .v-input__slot {
    border: 1.5px solid #ccc;
    border-radius: 8px;
    font-size: 14px;
  }
}

.flex-grow-none {
  flex-grow: 0;
}
</style>
